import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Settings, CreditCard, HelpCircle, LogOut, Wrench, DollarSign, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
export function ProfileDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    signOut
  } = useAuth();
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  const handleLogout = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/');
    showToast('Logged out successfully', 'success');
  };
  // Shared styles for menu items
  const menuItemBase = `
    flex items-center gap-3 px-4 py-2.5 text-sm font-bold uppercase tracking-wider
    transition-all duration-200 ease-out
    focus:outline-none focus-visible:bg-gray-900 focus-visible:text-white
  `;
  const menuItemInteractive = `
    ${menuItemBase}
    text-gray-400 
    hover:bg-gray-900 hover:text-white
    active:bg-gray-800
  `;
  return <div className="relative" ref={dropdownRef} data-id="element-818">
      {/* Trigger Button */}
      <button onClick={() => setIsOpen(!isOpen)} className={`
          relative flex items-center justify-center
          w-10 h-10 border
          transition-all duration-200 ease-out
          focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF0000] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]
          ${isOpen ? 'bg-[#FF0000]/10 border-[#FF0000]/50 text-[#FF0000]' : 'bg-[#0a0a0a] border-gray-700 text-gray-400 hover:text-white hover:border-[#FF0000]/50'}
        `} aria-label="User menu" aria-expanded={isOpen} aria-haspopup="true" data-id="element-819">
        <User size={18} strokeWidth={2} data-id="element-820" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && <div className="
            absolute right-0 mt-2 w-64
            bg-[#0a0a0a] 
            shadow-2xl shadow-black/50
            border border-gray-800
            py-1.5 z-50
            origin-top-right
            animate-in fade-in-0 zoom-in-95 slide-in-from-top-2
            duration-200 ease-out
          " role="menu" aria-orientation="vertical" data-id="element-821">
          {/* Section Header */}
          <div className="px-4 py-2.5 bg-[#050505] border-b border-gray-800" data-id="element-822">
            <p className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-823">
              Account
            </p>
          </div>

          {/* Menu Items */}
          <div className="py-1" data-id="element-824">
            <Link to="/settings" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-825">
              <Settings size={16} strokeWidth={2} className="text-gray-600" data-id="element-826" />
              <span data-id="element-827">Settings</span>
            </Link>

            {/* Affiliate Program - Disabled */}
            <div className={`
                ${menuItemBase}
                justify-between
                text-gray-600 cursor-not-allowed
              `} role="menuitem" aria-disabled="true" data-id="element-828">
              <div className="flex items-center gap-3" data-id="element-829">
                <DollarSign size={16} strokeWidth={2} className="text-gray-600" data-id="element-830" />
                <span data-id="element-831">Affiliate Program</span>
              </div>
              <span className="text-[10px] font-bold bg-gray-900 text-gray-600 px-2 py-0.5 border border-gray-800" data-id="element-832">
                SOON
              </span>
            </div>

            <Link to="/premium-tools" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-833">
              <Wrench size={16} strokeWidth={2} className="text-gray-600" data-id="element-834" />
              <span data-id="element-835">Tools</span>
            </Link>
          </div>

          {/* Divider */}
          <div className="my-1.5 mx-3 border-t border-gray-800" data-id="element-836" />

          {/* Billing & Support Section */}
          <div className="py-1" data-id="element-837">
            <Link to="/settings?tab=billing" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-838">
              <CreditCard size={16} strokeWidth={2} className="text-gray-600" data-id="element-839" />
              <span data-id="element-840">Billing</span>
            </Link>

            <a href="mailto:support@toxicdetector.com" className={menuItemInteractive} onClick={() => setIsOpen(false)} role="menuitem" data-id="element-841">
              <HelpCircle size={16} strokeWidth={2} className="text-gray-600" data-id="element-842" />
              <span data-id="element-843">Support</span>
            </a>
          </div>

          {/* Divider */}
          <div className="my-1.5 mx-3 border-t border-gray-800" data-id="element-844" />

          {/* Logout */}
          <div className="py-1" data-id="element-845">
            <button onClick={handleLogout} className={`
                ${menuItemBase}
                w-full text-left
                text-[#FF0000]
                hover:bg-[#FF0000]/10 hover:text-[#FF0000]
                active:bg-[#FF0000]/20
              `} role="menuitem" data-id="element-846">
              <LogOut size={16} strokeWidth={2} data-id="element-847" />
              <span data-id="element-848">Logout</span>
            </button>
          </div>
        </div>}
    </div>;
}